import { useFormContext } from 'react-hook-form';
import { Icon } from '@scripsteam/scrips-signal-ds';
import { useAvailableSlots } from '@/services/scheduling/use-available-slots';
import { NO_SLOT_AVAILABILITY_MESSAGE } from '@/scheduling/lib/appointment-strings';
import type { AppointmentFormValues } from '@/types/appointment-form';
import { practiceNow } from '@/shared/lib/practice-time';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

interface TimeSlotsSectionProps {
  practiceId: string;
  /** Open the full Time & Date sub-page (Edit pencil on the selected card). */
  onOpenTimeDate?: () => void;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmt12h(iso: string) {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'p' : 'a';
  return `${h > 12 ? h - 12 : h || 12}:${m}${ampm}`;
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  return `${MONTH_SHORT[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}, ${d.getFullYear()}`;
}

/**
 * TimeSlotsSection — slot picker for the selected provider+profile+date.
 * Mirrors Flutter `date_time_widget.dart` slot [2]:
 *   - When `appointment.start == null` → show the first 1–2 slot chips
 *     (we render the full grid here for usability)
 *   - When a slot is picked → show a single "selected slot" card with
 *     date + time and Edit / Delete actions
 */
export function TimeSlotsSection({ practiceId, onOpenTimeDate }: TimeSlotsSectionProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const providerId = watch('providerId');
  const selectedDate = watch('selectedDate');
  const selectedSlotId = watch('slotId');
  const slotStart = watch('slotStart');
  const appointmentProfileId = watch('appointmentProfileId');
  const location = watch('location');

  const { data: rawSlots = [], isLoading } = useAvailableSlots(
    providerId && practiceId && selectedDate && appointmentProfileId
      ? {
          practiceId,
          practitionerId: providerId,
          appointmentProfileId,
          start: selectedDate,
          end: new Date(new Date(selectedDate).setHours(23, 59, 59)).toISOString(),
          count: 100,
        }
      : null,
  );

  // Drop slots whose start time has already passed — when the user
  // picks today, anything earlier than `now` isn't bookable. Future
  // dates pass through unchanged because Date.parse(s.start) > now.
  const nowMs = practiceNow().getTime();
  const slots = rawSlots.filter((s) => s.start && Date.parse(s.start) >= nowMs);

  const clearSlot = () => {
    setValue('slotId', null, { shouldValidate: true });
    setValue('slotStart', null);
    setValue('slotEnd', null);
  };

  return (
    <div className="booking-section" data-slot="booking-section">
      <p className="booking-section-label">
        Date &amp; time
      </p>

      {selectedSlotId && slotStart ? (
        // The design's chosen slot (`.slotChosen`): calendar icon, the date in meta over the time in card-title type,
        // edit and clear icon buttons. Flutter date_time_widget.dart:52-117.
        <div className="booking-slot-chosen">
          <Icon name="calendar" size={20} aria-hidden />
          <div className="booking-grow">
            <p className="booking-slot-date">{fmtDate(slotStart)}</p>
            <p className="booking-slot-time-big">{fmt12h(slotStart)}</p>
          </div>
          {onOpenTimeDate ? (
            <button type="button" onClick={onOpenTimeDate} className="booking-icon-btn" aria-label="Edit time">
              <Icon name="edit" size={16} aria-hidden />
            </button>
          ) : null}
          <button type="button" onClick={clearSlot} className="booking-icon-btn" data-tone="error" aria-label="Clear slot">
            <Icon name="close" size={16} aria-hidden />
          </button>
        </div>
      ) : isLoading ? (
        <div className="booking-slot-row">
          <span className="booking-slot-skeleton" />
          <span className="booking-slot-skeleton" />
        </div>
      ) : slots.length === 0 ? (
        // Even when the inline picker has nothing to show, keep the
        // right-arrow so the user can still open the full Time & Date
        // sub-page and try a different date / location.
        <div className="booking-slot-row">
          <p className="booking-no-slots">{NO_SLOT_AVAILABILITY_MESSAGE}</p>
          {onOpenTimeDate ? (
            <button type="button" onClick={onOpenTimeDate} className="booking-slot-arrow" aria-label="See all slots">
              <Icon name="forward" size={16} aria-hidden />
            </button>
          ) : null}
        </div>
      ) : (
        // Flutter parity (date_time_widget.dart:119-196): the first two
        // slots as the design's slot cards (`.slot`: date, time, location word)
        // plus the round arrow that opens the full Time & Date sub-page.
        <div className="booking-slot-row">
          {slots.slice(0, 2).map((slot, i) => {
            const slotKey = slot.start ?? `slot-${i}`;
            const isSelected = selectedSlotId === slotKey;
            return (
              <button
                key={slotKey}
                type="button"
                onClick={() => {
                  if (!slot.start) return;
                  setValue('slotId', slotKey, { shouldValidate: true });
                  setValue('slotStart', slot.start);
                  setValue('slotEnd', slot.end);
                }}
                data-selected={isSelected || undefined}
                className="booking-slot"
              >
                <span className="booking-slot-date">{slot.start ? fmtDate(slot.start) : '—'}</span>
                <span className="booking-slot-time">{slot.start ? fmt12h(slot.start) : '—'}</span>
                <span className="booking-slot-loc">{LOCATION_LABEL[location?.name ?? ''] ?? location?.name ?? ''}</span>
              </button>
            );
          })}
          {onOpenTimeDate ? (
            <button type="button" onClick={onOpenTimeDate} className="booking-slot-arrow" aria-label="See all slots">
              <Icon name="forward" size={16} aria-hidden />
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** The design's location words (SchedBookingForm `LOCATION_LABEL`), same map as the location tiles. */
const LOCATION_LABEL: Record<string, string> = { Office: 'In clinic', Virtual: 'Video', Home: 'Home', Block: 'Block' };
